/**
 * Геометрия просмотра фотографии во весь экран (щипок, перетаскивание, двойное касание).
 *
 * <p>Модель проще, чем у редактора аватара: кадрировать здесь нечего, снимок только
 * рассматривают. Поэтому состояние — три числа, а единственное правило звучит так:
 * «не отпускать картинку от экрана». При масштабе 1 сдвиг запрещён вовсе, при большем —
 * ограничен ровно той частью снимка, что вылезла за края окна.
 *
 * <p>Вынесено из компонента отдельным модулем по той же причине, что и
 * `avatarCropRect`: арифметику границ надо уметь проверить без экрана и жестов.
 */

export type PhotoZoomViewport = {
  /** Масштаб относительно «снимок вписан в окно». */
  scale: number;
  /** Сдвиг в экранных пикселях от центра окна. */
  tx: number;
  ty: number;
};

export const IDENTITY_PHOTO_ZOOM: PhotoZoomViewport = { scale: 1, tx: 0, ty: 0 };

/** Меньше единицы не отдаляем: пустые поля вокруг снимка не нужны никому. */
const MIN_PHOTO_ZOOM = 1;

/**
 * Аватар приходит квадратом 512 px (`SquareAvatarImageProcessor.OUTPUT_EDGE_PX`), и на
 * телефоне он уже растянут почти во всю ширину. Вчетверо — предел, за которым видны
 * только пиксели JPEG, а не лицо.
 */
export const MAX_PHOTO_ZOOM = 4;

/**
 * Предел для снимка из переписки. Больше аватарного, и намеренно: в чат присылают не только
 * лица, но и снимки документов и скриншоты переписки — то, что открывают именно затем, чтобы
 * прочитать. Вписанный в экран лист А4 даёт кегль в несколько пикселей, и вчетверо его не
 * хватает; при этом файл приходит в исходном разрешении, так что увеличивать есть что.
 */
export const MAX_CHAT_PHOTO_ZOOM = 10;

/** Куда прыгает двойное касание с исходного масштаба. */
export const DOUBLE_TAP_PHOTO_ZOOM = 2.5;

function clampNumber(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  if (value < min) return min;
  if (value > max) return max;
  return value;
}

/** Отсечение по нулевой границе даёт `-0`; трансформам всё равно, а сравнениям в тестах — нет. */
function withoutNegativeZero(value: number): number {
  return value === 0 ? 0 : value;
}

/**
 * Приводит состояние к допустимому: масштаб в границах, сдвиг — в пределах вылезшей
 * за окно части снимка.
 *
 * @param photoSide сторона снимка на экране при масштабе 1 (он квадратный)
 */
export function clampPhotoZoom(
  next: PhotoZoomViewport,
  photoSide: number,
  boxWidth: number,
  boxHeight: number,
): PhotoZoomViewport {
  return clampPhotoZoomRect(next, photoSide, photoSide, boxWidth, boxHeight);
}

/**
 * То же правило для снимка произвольных пропорций.
 *
 * <p>Квадратной здесь остаётся только фотография из профиля: сервер режет её сам
 * (`SquareAvatarImageProcessor`). Снимок из переписки приходит как есть — вертикальный,
 * горизонтальный, лист документа, — и границы сдвига у него по каждой оси свои. Считать их
 * по меньшей стороне, как для аватара, значило бы запретить довести до края ровно ту часть
 * кадра, ради которой снимок и приближают.
 *
 * @param contentWidth ширина снимка на экране при масштабе 1 (уже вписанного в окно)
 * @param contentHeight высота там же
 * @param maxScale предел приближения; по умолчанию аватарный
 */
export function clampPhotoZoomRect(
  next: PhotoZoomViewport,
  contentWidth: number,
  contentHeight: number,
  boxWidth: number,
  boxHeight: number,
  maxScale: number = MAX_PHOTO_ZOOM,
): PhotoZoomViewport {
  const top = Number.isFinite(maxScale) && maxScale > MIN_PHOTO_ZOOM ? maxScale : MIN_PHOTO_ZOOM;
  const scale = clampNumber(next.scale, MIN_PHOTO_ZOOM, top);
  const w = Number.isFinite(contentWidth) && contentWidth > 0 ? contentWidth : 0;
  const h = Number.isFinite(contentHeight) && contentHeight > 0 ? contentHeight : 0;
  // Половина того, что не поместилось. Если снимок уже окна, сдвигать по этой оси нечего.
  const maxX = Math.max(0, (w * scale - boxWidth) / 2);
  const maxY = Math.max(0, (h * scale - boxHeight) / 2);
  return {
    scale,
    tx: withoutNegativeZero(clampNumber(next.tx, -maxX, maxX)),
    ty: withoutNegativeZero(clampNumber(next.ty, -maxY, maxY)),
  };
}

/**
 * Размер снимка, вписанного в окно с сохранением пропорций, — то, что делает
 * `resizeMode="contain"`. Нужен здесь потому, что от него считаются границы сдвига: RN
 * вписывает кадр сам и своих чисел наружу не отдаёт.
 *
 * <p>Пропорции неизвестны (файл ещё качается) — отдаём окно целиком: при масштабе 1 сдвиг
 * всё равно запрещён, а как только `Image.getSize` ответит, числа станут настоящими.
 */
export function photoFitSize(
  naturalWidth: number,
  naturalHeight: number,
  boxWidth: number,
  boxHeight: number,
): { width: number; height: number } {
  const bw = Number.isFinite(boxWidth) && boxWidth > 0 ? boxWidth : 0;
  const bh = Number.isFinite(boxHeight) && boxHeight > 0 ? boxHeight : 0;
  const nw = Number.isFinite(naturalWidth) && naturalWidth > 0 ? naturalWidth : 0;
  const nh = Number.isFinite(naturalHeight) && naturalHeight > 0 ? naturalHeight : 0;
  if (bw === 0 || bh === 0 || nw === 0 || nh === 0) return { width: bw, height: bh };
  const k = Math.min(bw / nw, bh / nh);
  return { width: nw * k, height: nh * k };
}

/**
 * Двойное касание: приблизить, а если уже приближено — вернуть как было. Именно
 * «вернуть», а не «приблизить ещё»: второе касание в мессенджерах всегда означает
 * «покажи снимок целиком».
 */
export function toggleDoubleTapZoom(
  current: PhotoZoomViewport,
  /** Куда приближать. Снимку из переписки нужен прыжок побольше — см. `MAX_CHAT_PHOTO_ZOOM`. */
  target: number = DOUBLE_TAP_PHOTO_ZOOM,
): PhotoZoomViewport {
  if (current.scale > MIN_PHOTO_ZOOM + 0.01) {
    return IDENTITY_PHOTO_ZOOM;
  }
  return { scale: target, tx: 0, ty: 0 };
}

/** Сторона квадратного снимка, вписанного в окно. */
export function photoSideForBox(boxWidth: number, boxHeight: number): number {
  const w = Number.isFinite(boxWidth) && boxWidth > 0 ? boxWidth : 0;
  const h = Number.isFinite(boxHeight) && boxHeight > 0 ? boxHeight : 0;
  return Math.min(w, h);
}

/**
 * Отступ рамки от краёв окна. Медальон, доведённый до самой кромки экрана, читается как
 * обрезанный краем, а не как оправа: воздух вокруг — и есть то, что делает его рамкой.
 */
export const PHOTO_FRAME_INSET = 24;

/**
 * Сторона квадрата, в котором показывают фотографию в рамке.
 *
 * <p>Рамка — это окно просмотра: снимок ходит и приближается внутри неё, а сама она стоит
 * на месте (см. `UserPhotoViewer`). Значит, её сторона — не только про красоту полей, но и
 * про арифметику сдвига: именно от неё считаются границы в {@link clampPhotoZoomRect}.
 *
 * <p>Отступы по осям разные не для симметрии, а потому что сверху и снизу над снимком стоят
 * имя с крестиком и подсказка: медальон, центрированный по всему экрану, на узком окне
 * уезжал бы под них.
 */
export function photoFrameSideForBox(
  boxWidth: number,
  boxHeight: number,
  sideInset: number = PHOTO_FRAME_INSET,
  verticalInset: number = sideInset,
): number {
  const si = Number.isFinite(sideInset) && sideInset > 0 ? sideInset : 0;
  const vi = Number.isFinite(verticalInset) && verticalInset > 0 ? verticalInset : 0;
  return photoSideForBox(boxWidth - 2 * si, boxHeight - 2 * vi);
}
